import { NavigateNext } from '@mui/icons-material';
import { Box, Breadcrumbs, Button, Typography } from '@mui/material';
import './style.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { NavItem } from '../interfaces/userContext';
import { useState } from 'react';
import { NEW_SCREEN_ENABLED } from '../config/featureFlags';

interface IBreadcrumb {
  key: number;
  title: string;
  path: string;
  params?: { [key: string]: number | string | null };
}

interface IBreadcrumbProps {
  navItems: NavItem[];
  children?: React.ReactNode | any;
}

const Breadcrumb = (props: IBreadcrumbProps) => {
  const location = useLocation(); // Get the current URL location
  const navigate = useNavigate(); // Hook for navigation
  const urlSegments = location.pathname.split('/').filter(Boolean); // Split the URL path into segments
  const queryParams = new URLSearchParams(location.search); // Parse query parameters
  const currentPath = location.pathname
  const currentNavItem = props.navItems.find(item => currentPath.endsWith(item.path))

  // Don't render breadcrumb for track-trace base route, but allow ambient and grocery
  if (currentPath === '/vision/track-trace') {
    return null;
  }

  if (currentPath.includes('/beacon')) {
    return null;
  }

  if (currentNavItem && !NEW_SCREEN_ENABLED.includes(currentNavItem?.product)) {
    return null
  }

  // Custom breadcrumb handling for track-trace routes
  let breadcrumbLinks: IBreadcrumb[] = [];
  
  if (currentPath.includes('/track-trace/ambient')) {
    // Custom breadcrumb for track-trace/ambient: Vision > Track & Trace > Ambient
    breadcrumbLinks = [
      { key: 1, title: 'Vision', path: '/vision/' },
      { key: 2, title: 'Track & Trace', path: '/vision/track-trace' },
      { key: 3, title: 'Ambient', path: '/vision/track-trace/ambient' }
    ];
  } else if (currentPath.includes('/track-trace/grocery')) {
    // Custom breadcrumb for track-trace/grocery: Vision > Track & Trace > Grocery
    breadcrumbLinks = [
      { key: 1, title: 'Vision', path: '/vision/' },
      { key: 2, title: 'Track & Trace', path: '/vision/track-trace' },
      { key: 3, title: 'Grocery', path: '/vision/track-trace/grocery' }
    ];
  } else {
    // Dynamically construct breadcrumb links from URL segments for other routes
    breadcrumbLinks = urlSegments
      .map((segment, index) => {
        const path = `/${urlSegments.slice(0, index + 1).join('/')}`; // Reconstruct the path up to this segment

        const navSegment = props.navItems.find(item => path.endsWith(item.path)); // Find matching navSegment
        if (!navSegment && index > 0) {
          return null; // Skip this breadcrumb if no matching path is found
        }

        const title = navSegment ? navSegment.name : decodeURIComponent(segment); // Use the segment as the title
        return { key: index + 1, title, path };
      })
      .filter(Boolean) as IBreadcrumb[]; // Remove null values from the array
  }

  // Add query parameters as the last breadcrumb if needed
  if (queryParams.toString()) {
    breadcrumbLinks.push({
      key: breadcrumbLinks.length + 1,
      title: queryParams.toString(),
      path: location.pathname + location.search,
    });
  }

  // Sanitize path using URL constructor to prevent open redirect (CWE-601)
  // Returns only the pathname if same-origin, otherwise null
  const sanitizePath = (path: string): string | null => {
    try {
      const url = new URL(path, window.location.origin);
      if (url.origin !== window.location.origin) return null;
      return url.pathname;
    } catch {
      return null;
    }
  };

  const handleClick = (path: string, params?: { [key: string]: string | number | null }) => {
    const safePath = sanitizePath(path); // Break taint chain — extract clean pathname only
    if (!safePath) return;
    const queryParams = new URLSearchParams(params as Record<string, string>).toString(); // Convert params to query string
    const fullPath = queryParams ? `${safePath}?${queryParams}` : safePath; // Append query string to path if params exist
    navigate(fullPath); // Navigate to the constructed path
  };

  if (location.pathname === "/vision/") {
    return null;
  }

  return (
    <div className="vision-breadcrumb-background">
      <Box display="flex" className="breadcrumb-link-container" style={{ width: '100%' }}>
        <Box flexGrow={1}>
          <Breadcrumbs
            separator={<NavigateNext fontSize="small" />}
            aria-label="breadcrumb"
          >
            {breadcrumbLinks.filter((link): link is IBreadcrumb => link !== null).map(({ title, path, params }: IBreadcrumb, index) => {
              const isLast = index === breadcrumbLinks.length - 1; // Check if it's the last breadcrumb
              return isLast ? (
                <Typography
                  key={`${title}${path}`}
                  color="inherit"
                  data-testid="breadcrumb-last-text"
                  style={{ textTransform: 'capitalize' }}
                >
                  {title}
                </Typography>
              ) : (
                <Button
                  key={`${title}${path}`}
                  onClick={() => handleClick(path, params)} // Pass path and params to handleClick
                  data-testid="breadcrumb-link-button"
                >
                  {title}
                </Button>
              );
            })}
          </Breadcrumbs>
        </Box>
        {props.children && <Box pr={4}>{props.children}</Box>}
      </Box>
    </div>
  );
};

export default Breadcrumb;